import { useEffect, useMemo, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import geojsonvt from 'geojson-vt';
import {
  PARCEL_STYLE_BY_STATUS,
  PARCEL_STYLE_HIGHLIGHT,
  LABEL_MIN_PX,
  LABEL_FONT,
  getParcelRef,
  formatParcelLabel,
  type ParcelRecord,
  type ParcelStatus,
} from '@/utils/parcelGeometry';
// Legacy JS util — worst-wins hazard → label colour.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import { labelBgForHazards, labelFgForBg } from '@/utils/hazardColor';

/**
 * High-performance parcel renderer.
 *
 * Instead of mounting one Leaflet layer per parcel (which froze the main
 * thread for ~20s on a few thousand parcels), this slices the parcels into
 * vector tiles with geojson-vt and paints only the *visible* tiles onto
 * canvas via a custom `L.GridLayer`. Toggling the layer on now renders just
 * the handful of on-screen tiles, so the interaction paints in milliseconds
 * regardless of dataset size. Labels are drawn straight to the tile canvas
 * (no DOM tooltips), and clicks are hit-tested against the parcel geometry
 * to open a popup.
 */

const TILE_EXTENT = 4096;

interface ParcelVectorTilesProps {
  parcels: ParcelRecord[];
  /**
   * ref → membership/payment status, driving the parcel colour ramp. Refs
   * absent from the map (and a null map entirely) paint as `unlinked`.
   */
  statusByRef?: ReadonlyMap<string, ParcelStatus> | null;
  /** A single parcel ref to highlight with the purple outline style. */
  highlightRef?: string | null;
  /** ref → hazard tags; when present a parcel's label gets a hazard chip. */
  hazardByRef?: ReadonlyMap<string, string[]> | null;
  /** Draw ParcelNo labels (subject to the size-LOD test). */
  showLabels: boolean;
  /** Below this zoom, labels are never drawn. */
  labelZoom: number;
  /** When false, no click hit-test is bound (parcels are non-interactive). */
  interactive?: boolean;
  /** Click handler — receives the hit parcel + latlng. */
  onParcelClick?: (parcel: ParcelRecord, latlng: L.LatLng) => void;
}

interface DrawOpts {
  statusByRef: ReadonlyMap<string, ParcelStatus> | null;
  highlightRef: string | null;
  hazardByRef: ReadonlyMap<string, string[]> | null;
  showLabels: boolean;
  tileZ: number;
  tileX: number;
  tileY: number;
}

// One outer ring per polygon, in [lng, lat] order, plus a bbox — used for
// click hit-testing without re-walking the full GeoJSON each time.
interface HitParcel {
  parcel: ParcelRecord;
  ref: string | null;
  bbox: [number, number, number, number]; // minLng, minLat, maxLng, maxLat
  rings: [number, number][][]; // outer rings only (holes ignored for hit-test)
}

function buildFeatureCollection(parcels: ParcelRecord[]) {
  return {
    type: 'FeatureCollection' as const,
    features: parcels.flatMap((p) => {
      if (!p.geometry?.coordinates) return [];
      return [
        {
          type: 'Feature' as const,
          geometry: p.geometry as GeoJSON.Geometry,
          properties: {
            ref: getParcelRef(p),
            label: formatParcelLabel(p),
            // True centroid stored so the tile renderer can reliably place
            // exactly one label per parcel (the clipped-bbox approach draws
            // duplicates for large parcels that span tile boundaries).
            centroid_lat: p.centroid_lat ?? null,
            centroid_lng: p.centroid_lon ?? null,
          },
        },
      ];
    }),
  };
}

// Web-Mercator tile-corner → geographic coordinate helpers.
function tileToLng(x: number, z: number): number {
  return (x / 2 ** z) * 360 - 180;
}
function tileToLat(y: number, z: number): number {
  const n = Math.PI - (2 * Math.PI * y) / 2 ** z;
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
}

function buildHitIndex(parcels: ParcelRecord[]): HitParcel[] {
  const out: HitParcel[] = [];
  for (const p of parcels) {
    const geom = p.geometry;
    if (!geom?.coordinates) continue;
    let rings: [number, number][][] = [];
    if (geom.type === 'Polygon') {
      rings = [(geom.coordinates as [number, number][][])[0] ?? []];
    } else if (geom.type === 'MultiPolygon') {
      rings = (geom.coordinates as [number, number][][][]).map((poly) => poly[0] ?? []);
    } else {
      continue;
    }
    let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
    for (const ring of rings) {
      for (const [lng, lat] of ring) {
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      }
    }
    out.push({
      parcel: p,
      ref: getParcelRef(p),
      bbox: [minLng, minLat, maxLng, maxLat],
      rings,
    });
  }
  return out;
}

function pointInRing(lng: number, lat: number, ring: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const pi = ring[i];
    const pj = ring[j];
    if (!pi || !pj) continue;
    const [xi, yi] = pi;
    const [xj, yj] = pj;
    const intersect =
      yi > lat !== yj > lat &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi || Number.EPSILON) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function drawTile(
  ctx: CanvasRenderingContext2D,
  features: Array<{ type: number; geometry: [number, number][][]; tags: Record<string, unknown> }>,
  sizePx: number,
  opts: DrawOpts,
) {
  const scale = sizePx / TILE_EXTENT;
  for (const f of features) {
    if (f.type !== 3) continue; // polygons only
    const ref = (f.tags.ref as string) ?? '';
    // Colour comes from /member-status alone, never from the link set. Until
    // that fetch lands every parcel paints `unlinked` — which is also the
    // quietest style (0.1 grey), so the brief flash reads as "still loading"
    // rather than mis-stating someone's payment status. There is no honest
    // colour on a four-step ramp for "linked, payment unknown".
    const status: ParcelStatus =
      (ref ? opts.statusByRef?.get(ref) : undefined) ?? 'unlinked';
    const highlighted = Boolean(ref && opts.highlightRef && ref === opts.highlightRef);
    const style = highlighted
      ? PARCEL_STYLE_HIGHLIGHT
      : PARCEL_STYLE_BY_STATUS[status];

    ctx.beginPath();
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const ring of f.geometry) {
      for (let i = 0; i < ring.length; i++) {
        const pt = ring[i];
        if (!pt) continue;
        const px = pt[0] * scale;
        const py = pt[1] * scale;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
        if (px < minX) minX = px;
        if (px > maxX) maxX = px;
        if (py < minY) minY = py;
        if (py > maxY) maxY = py;
      }
      ctx.closePath();
    }
    ctx.fillStyle = style.fillColor;
    ctx.globalAlpha = style.fillOpacity;
    ctx.fill('evenodd');
    ctx.globalAlpha = 1;
    ctx.lineWidth = style.weight;
    ctx.strokeStyle = style.color;
    ctx.stroke();

    // Label: drawn only when enabled and the parcel is large enough on-screen.
    //
    // Centroid-in-tile guard (de-duplication): geojson-vt buffers edge
    // parcels into neighbouring tiles, so a large parcel can appear in
    // several tiles simultaneously. We draw the label ONLY in the tile whose
    // geographic extent contains the true parcel centroid (centroid_lat /
    // centroid_lng stored as feature tags). The clipped-bbox centroid used
    // previously was unreliable for parcels that span tile boundaries.
    const label = f.tags.label as string;
    if (opts.showLabels && label) {
      const w = maxX - minX;
      const h = maxY - minY;

      // Project the true centroid into this tile's pixel space.
      const cLat = f.tags.centroid_lat as number | null;
      const cLng = f.tags.centroid_lng as number | null;
      let labelX: number;
      let labelY: number;
      let centreInTile: boolean;
      if (cLat != null && cLng != null) {
        const tileLng0 = tileToLng(opts.tileX, opts.tileZ);
        const tileLng1 = tileToLng(opts.tileX + 1, opts.tileZ);
        const tileLat0 = tileToLat(opts.tileY, opts.tileZ);     // north edge
        const tileLat1 = tileToLat(opts.tileY + 1, opts.tileZ); // south edge
        labelX = ((cLng - tileLng0) / (tileLng1 - tileLng0)) * sizePx;
        labelY = ((tileLat0 - cLat) / (tileLat0 - tileLat1)) * sizePx;
        centreInTile = labelX >= 0 && labelX < sizePx && labelY >= 0 && labelY < sizePx;
      } else {
        // Fallback for parcels without a stored centroid.
        labelX = (minX + maxX) / 2;
        labelY = (minY + maxY) / 2;
        centreInTile = labelX >= 0 && labelX <= sizePx && labelY >= 0 && labelY <= sizePx;
      }

      if (centreInTile && Math.min(w, h) >= LABEL_MIN_PX) {
        ctx.font = LABEL_FONT;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        const hazards = ref && opts.hazardByRef ? opts.hazardByRef.get(ref) : null;
        // Always draw a background chip — hazard colour when tags exist,
        // neutral white otherwise so the number is legible on any fill.
        // `labelBgForHazards` returns null for tags it has no colour for, so
        // coalesce as well as branch — otherwise the chip paints with a null
        // fillStyle and the number sits on whatever the last fill was.
        const bg =
          (hazards && hazards.length ? labelBgForHazards(hazards) : null) ??
          'rgba(255,255,255,0.82)';
        const fg = hazards && hazards.length ? labelFgForBg(bg) : '#1b1b1b';

        const tw = ctx.measureText(label).width;
        const chipW = tw + 8;
        const chipH = 14;
        const rx = labelX - chipW / 2;
        const ry = labelY - chipH / 2;
        const r = 3;
        ctx.beginPath();
        ctx.moveTo(rx + r, ry);
        ctx.arcTo(rx + chipW, ry, rx + chipW, ry + chipH, r);
        ctx.arcTo(rx + chipW, ry + chipH, rx, ry + chipH, r);
        ctx.arcTo(rx, ry + chipH, rx, ry, r);
        ctx.arcTo(rx, ry, rx + chipW, ry, r);
        ctx.closePath();
        ctx.fillStyle = bg;
        ctx.fill();

        ctx.fillStyle = fg;
        ctx.fillText(label, labelX, labelY);
      }
    }
  }
}

export function ParcelVectorTiles({
  parcels,
  statusByRef = null,
  highlightRef = null,
  hazardByRef = null,
  showLabels,
  labelZoom,
  interactive = true,
  onParcelClick,
}: ParcelVectorTilesProps) {
  const map = useMap();

  // Slice once per parcel set. geojson-vt indexes thousands of features in a
  // few ms and getTile() is O(visible features) — the expensive per-feature
  // layer creation of the old path is gone entirely.
  const tileIndex = useMemo(() => {
    const fc = buildFeatureCollection(parcels);
    return geojsonvt(fc, {
      maxZoom: 22,
      tolerance: 3,
      extent: TILE_EXTENT,
      buffer: 64,
      indexMaxZoom: 5,
    });
  }, [parcels]);

  const hitIndex = useMemo(() => buildHitIndex(parcels), [parcels]);

  // Live options read fresh inside createTile. The label zoom-gate is
  // evaluated against `map.getZoom()` at draw time (not pre-baked) so tiles
  // recreated by a zoom always get the correct label flag with no React race.
  const optsRef = useRef({ statusByRef, highlightRef, hazardByRef, showLabels, labelZoom });
  optsRef.current = { statusByRef, highlightRef, hazardByRef, showLabels, labelZoom };

  // Create the canvas GridLayer once per slice; re-create when the parcel set
  // changes. Stored in a ref so the redraw / hit-test effects can reach it.
  const layerRef = useRef<L.GridLayer | null>(null);

  useEffect(() => {
    if (!map.getPane('parcelTiles')) {
      map.createPane('parcelTiles');
      const pane = map.getPane('parcelTiles');
      // Above the base tiles (200) but below boundary outlines / markers (400+).
      if (pane) pane.style.zIndex = '250';
    }

    const GridProto = L.GridLayer.extend({
      createTile(coords: L.Coords) {
        const size = this.getTileSize();
        const tile = document.createElement('canvas');
        const dpr = window.devicePixelRatio || 1;
        tile.width = size.x * dpr;
        tile.height = size.y * dpr;
        tile.style.width = `${size.x}px`;
        tile.style.height = `${size.y}px`;
        const ctx = tile.getContext('2d');
        const t = tileIndex.getTile(coords.z, coords.x, coords.y);
        if (ctx && t && t.features.length) {
          ctx.scale(dpr, dpr);
          const o = optsRef.current;
          drawTile(
            ctx,
            t.features as unknown as Array<{
              type: number;
              geometry: [number, number][][];
              tags: Record<string, unknown>;
            }>,
            size.x,
            {
              statusByRef: o.statusByRef,
              highlightRef: o.highlightRef,
              hazardByRef: o.hazardByRef,
              showLabels: o.showLabels && map.getZoom() >= o.labelZoom,
              tileZ: coords.z,
              tileX: coords.x,
              tileY: coords.y,
            },
          );
        }
        return tile;
      },
    });

    const layer = new (GridProto as unknown as new (opts: L.GridLayerOptions) => L.GridLayer)({
      pane: 'parcelTiles',
      updateWhenZooming: false,
      keepBuffer: 2,
    });
    layer.addTo(map);
    layerRef.current = layer;

    return () => {
      layer.remove();
      layerRef.current = null;
    };
  }, [map, tileIndex]);

  // Recolour / relabel without rebuilding the slice: redraw on status, hazard,
  // or label-visibility change. (Zoom changes already recreate tiles.)
  // `statusByRef` MUST stay in this list — drop it and a link/unlink leaves the
  // old colours painted until the next zoom.
  useEffect(() => {
    layerRef.current?.redraw();
  }, [statusByRef, highlightRef, hazardByRef, showLabels, labelZoom]);

  // Click → hit-test parcels → emit selection. Bound only when interactive,
  // so during drawing (or on context-only maps) clicks pass through to the
  // draw control instead of selecting a parcel. Iterating the hit index on
  // click is cheap (bbox prefilter, point-in-polygon only on candidates).
  useEffect(() => {
    if (!interactive || !onParcelClick) return undefined;
    const onClick = (e: L.LeafletMouseEvent) => {
      const { lng, lat } = e.latlng;
      for (const p of hitIndex) {
        const [minLng, minLat, maxLng, maxLat] = p.bbox;
        if (lng < minLng || lng > maxLng || lat < minLat || lat > maxLat) continue;
        if (p.rings.some((ring) => pointInRing(lng, lat, ring))) {
          onParcelClick(p.parcel, e.latlng);
          return;
        }
      }
    };
    map.on('click', onClick);
    return () => {
      map.off('click', onClick);
    };
  }, [map, hitIndex, interactive, onParcelClick]);

  return null;
}

export default ParcelVectorTiles;
